// src/contexts/AuthContext.jsx
import React, { createContext, useState, useEffect, useContext } from 'react';
import {
    createUserWithEmailAndPassword,
    signInWithEmailAndPassword,
    signOut,
    onAuthStateChanged,
    fetchSignInMethodsForEmail,
    setPersistence,
    browserLocalPersistence
} from 'firebase/auth';
import {
    doc,
    setDoc,
    getDoc,
    collection,
    addDoc,
    query,
    where,
    getDocs,
    serverTimestamp,
    updateDoc,
    arrayRemove
} from 'firebase/firestore';
import { supabaseDataService } from '../services/supabaseService';
import { auth, db } from '../services/firebaseConfig';
import _ from "lodash";

const AuthContext = createContext();

export function useAuth() {
    return useContext(AuthContext);
}

const RESERVED_SUBDOMAINS = ['app', 'www', 'api'];

const getSubdomain = () => {
    const hostname = window.location.hostname;

    if (hostname === 'localhost' || hostname === '127.0.0.1') {
        return null;
    }

    if (hostname.endsWith('.localhost')) {
        const parts = hostname.split('.');
        return parts.length > 1 ? parts[0] : null;
    }

    const parts = hostname.split('.');

    if (parts.length > 2) {
        const subdomain = parts[0];
        return RESERVED_SUBDOMAINS.includes(subdomain) ? null : subdomain;
    }

    return null;
};

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [userProfile, setUserProfile] = useState(null);
    const [userGroups, setUserGroups] = useState([]);
    const [loading, setLoading] = useState(true);
    const [authError, setAuthError] = useState(null);
    const [firebaseConfig, setFirebaseConfig] = useState(null);
    const [categories, setCategories] = useState([]);

    const [subdomainValidation, setSubdomainValidation] = useState({
        isValid: true,
        isValidating: true,
        subdomain: getSubdomain(),
        error: null
    });

    // Cache for groups to avoid repeated database calls
    const [groupsCache, setGroupsCache] = useState(null);
    const [groupsCacheTimestamp, setGroupsCacheTimestamp] = useState(null);
    const CACHE_DURATION = 5 * 60 * 1000;

    useEffect(() => {
        console.log('Setting up Firebase Auth listener...');

        setPersistence(auth, browserLocalPersistence).then(() => {
            console.log('Firebase Auth persistence configured');
        }).catch((error) => {
            console.error('Error setting Firebase Auth persistence:', error);
        });

        const unsubscribe = onAuthStateChanged(
            auth,
            async (firebaseUser) => {
                try {
                    console.log('Auth state changed:', firebaseUser ? 'User logged in' : 'User logged out');

                    if (firebaseUser) {
                        await loadUserProfile(firebaseUser.uid, firebaseUser.email);
                    } else {
                        setUser(null);
                        setUserProfile(null);
                        setUserGroups([]);
                    }

                    if (!categories || categories.length === 0) {
                        await loadCategories();
                    }

                    setAuthError(null); // Clear any previous errors
                } catch (error) {
                    console.error('Error in auth state change handler:', error);
                    setAuthError(error.message);
                } finally {
                    setLoading(false);
                }
            },
            (error) => {
                // Error callback for onAuthStateChanged
                console.error('Firebase Auth listener error:', error);
                setAuthError(error.message);
                setLoading(false);
            }
        );

        return () => {
            console.log('Cleaning up Firebase Auth listener');
            unsubscribe();
        };
    }, []);

    useEffect(() => {
        if (!loading) {
            validateSubdomain();
        }
    }, [loading]);

    const loadCategories = async () => {
        try {
            const configCollection = collection(db, 'config');
            const configSnapshot = await getDocs(configCollection);

            if (!configSnapshot.empty) {
                const firstDoc = configSnapshot.docs[0];
                const configData = firstDoc.data();
                const categorizePromptId = configData.CategorizePromptId;

                const categoryPrompt = await getDoc(doc(db, 'openAICategoryPrompt', categorizePromptId));

                if (categoryPrompt.exists()) {
                    const categoryPromptData = categoryPrompt.data();
                    setCategories(categoryPromptData.categories);
                } else {
                    console.log('categoryPromptData document does not exist');
                }
            } else {
                console.log('No config documents exist');
            }
        } catch (error) {
            console.error('Error loading categories:', error);
        }
    };

    // Enhanced function to load complete user profile
    const loadUserProfile = async (userId, email) => {
        try {
            console.log('Loading user profile for:', userId, ' email:',email);
            const userDoc = await getDoc(doc(db, 'users', userId));

            if (userDoc.exists()) {
                const userData = userDoc.data();

                let group = await supabaseDataService.getGroupForUser(userData.uid);
                console.log('[loadUserProfile]--group--->',group);
                if ( !group || group.length == 0 ){
                    console.log('No group found for user:',userData.uid);
                    const groupName = userData.firstName+userData.lastName+'sGroup';
                    console.log('Creating group for user:',userData.uid,' groupName:',groupName);
                    //await createGroup(groupName, userData.uid, 'Auto created group');
                    //group = await supabaseDataService.getGroupForUser(userData.uid);
                }
                console.log('[loadUserProfile]group----->',group);
                const xtragroups = await supabaseDataService.getGroupsForUser(userData.uid);
                const completeProfile = {
                    // Firebase Auth data
                    id: userData.uid,
                    uid: userData.uid,
                    email: email,
                    emailVerified: userData.emailVerified,

                    // Firestore user data
                    firstName: userData.firstName || '',
                    lastName: userData.lastName || '',
                    fullName: `${userData.firstName || ''} ${userData.lastName || ''}`.trim(),
                    phone: userData.phone || '',
                    address: userData.address || {},
                    groups: group[0].group_name,
                    groupData: [...group, ...xtragroups], // Store the full group object separately if needed
                    isGroupAdmin: group[0]?.group_members[0]?.role_id == 1 || false,
                    profileComplete: userData.profileComplete || false,
                    createdAt: userData.createdAt,
                    timeZone: userData.timeZone || 'Etc/UTC',
                    groupName: group[0].group_name,
                    groupId: group[0].group_id,
                    // Computed fields
                    displayName: userData.firstName ?
                        `${userData.firstName} ${userData.lastName || ''}`.trim() :
                        userData.email?.split('@')[0] || 'User'
                };

                setUser(completeProfile);
                console.log('userData.groups----222->',completeProfile.groups);
                console.log('groupData----222->',completeProfile.groupData);
                setUserGroups(completeProfile.groupData);

                console.log('User profile loaded:', completeProfile);
                return completeProfile;
            } else {
                console.log('User document does not exist, creating default...');
                await createDefaultUserProfile(userId);
            }
        } catch (error) {
            console.error('Error loading user profile:', error);
            throw error;
        }
    };

    // Helper to create default user profile
    const createDefaultUserProfile = async (userId) => {
        try {
            const currentUser = auth.currentUser;
            const defaultProfile = {
                uid: userId,
                email: currentUser?.email || '',
                firstName: '',
                lastName: '',
                groups: [],
                isGroupAdmin: false,
                createdAt: serverTimestamp(),
                profileComplete: false
            };

            await setDoc(doc(db, 'users', userId), defaultProfile);

            const completeProfile = {
                ...defaultProfile,
                fullName: '',
                displayName: currentUser?.email?.split('@')[0] || 'User',
                emailVerified: currentUser?.emailVerified || false,
                phone: '',
                address: {}
            };

            setUser(completeProfile);
            setUserGroups([]);

            console.log('Default user profile created:', completeProfile);
        } catch (error) {
            console.error('Error creating default user profile:', error);
            throw error;
        }
    };

    // Utility functions to access user data
    const getUserId = () => user?.uid;
    const getUserEmail = () => user?.email || userProfile?.email;
    const getUserDisplayName = () => userProfile?.displayName || user?.email?.split('@')[0] || 'User';
    const getUserFullName = () => userProfile?.fullName || '';
    const isEmailVerified = () => user?.emailVerified || false;
    const isGroupAdmin = () => user?.isGroupAdmin || false;

    const login = async (email, password, skipRedirect = false) => {
        try {
            console.log('Attempting login for:', email);
            setAuthError(null);

            const result = await signInWithEmailAndPassword(auth, email, password);
            console.log('Fetching user profile from Firestore for:', result.user.uid);

            const completeProfile = await loadUserProfile(result.user.uid, email);


            console.log('Login successful');
            return completeProfile;
        } catch (error) {
            console.error('Error logging in:', error.message);
            setAuthError(error.message);
            throw error;
        }
    };

    const checkAuthState = async () => {
        return new Promise((resolve) => {
            const unsubscribe = onAuthStateChanged(auth, (user) => {
                unsubscribe();
                resolve(user);
            });
        });
    };

    const logout = async () => {
        try {
            console.log('Logging out user');
            setAuthError(null);

            await signOut(auth);
            console.log('Logout successful');
        } catch (error) {
            console.error('Error logging out:', error.message);
            setAuthError(error.message);
            throw error;
        }
    };

    const signup = async (email, password, groupName, userProfile = {}) => {
        try {
            console.log('Attempting signup for:', email);
            setAuthError(null);
            const result = await createUserWithEmailAndPassword(auth, email, password);
            const firebaseUser = result.user;

            const sanitizedProfile = sanitizeUserProfile(userProfile);

            const userData = {
                email: firebaseUser.email,
                uid: firebaseUser.uid,
                isGroupAdmin: userProfile.newGroup,
                createdAt: serverTimestamp(),
                firstName: sanitizedProfile.firstName || '',
                lastName: sanitizedProfile.lastName || '',
                phone: sanitizedProfile.phone || '',
                // Address Information
                address: {
                    street: sanitizedProfile.address?.street || '',
                    city: sanitizedProfile.address?.city || '',
                    state: sanitizedProfile.address?.state || '',
                    zipCode: sanitizedProfile.address?.zipCode || '',
                    country: sanitizedProfile.address?.country || 'United States'
                },
                profileComplete: !!(sanitizedProfile.firstName && sanitizedProfile.lastName)
            };

            console.log('Creating user document in Firestore');
            console.log('User data:', userData);

            await setDoc(doc(db, 'users', firebaseUser.uid), userData, { merge: true });

            if (userProfile.newGroup) {
                await createGroup(groupName, firebaseUser.uid, 'group created by ' + sanitizedProfile.firstName + ' ' + sanitizedProfile.lastName);
            } else {
                await addUserToGroup(firebaseUser.uid, groupName);
            }

            console.log('Signup successful');
            return result;
        } catch (error) {
            console.error('Error signing up:', error.message);
            console.error('Full error:', error);
            setAuthError(error.message);
            throw error;
        }
    };

    const sanitizeUserProfile = (profile) => {
        const sanitized = {};

        // Sanitize string fields to remove any problematic characters
        if (profile.firstName) sanitized.firstName = String(profile.firstName).trim().slice(0, 100);
        if (profile.lastName) sanitized.lastName = String(profile.lastName).trim().slice(0, 100);
        if (profile.phone) sanitized.phone = String(profile.phone).trim().slice(0, 20);

        // Sanitize address object
        if (profile.address) {
            sanitized.address = {
                street: profile.address.street ? String(profile.address.street).trim().slice(0, 200) : '',
                city: profile.address.city ? String(profile.address.city).trim().slice(0, 100) : '',
                state: profile.address.state ? String(profile.address.state).trim().slice(0, 50) : '',
                zipCode: profile.address.zipCode ? String(profile.address.zipCode).trim().slice(0, 20) : '',
                country: profile.address.country ? String(profile.address.country).trim().slice(0, 100) : 'United States'
            };
        }

        return sanitized;
    };

    const updateUserProfile = async (profileData) => {
        try {
            console.log('Updating user profile for:', user?.uid);

            if (!user?.uid) {
                throw new Error('User not authenticated');
            }

            // Sanitize the profile data
            const sanitizedProfile = sanitizeUserProfile(profileData);

            // Prepare the update data
            const updateData = {
                firstName: sanitizedProfile.firstName || '',
                lastName: sanitizedProfile.lastName || '',
                phone: sanitizedProfile.phone || '',
                address: {
                    street: sanitizedProfile.address?.street || '',
                    city: sanitizedProfile.address?.city || '',
                    state: sanitizedProfile.address?.state || '',
                    zipCode: sanitizedProfile.address?.zipCode || '',
                    country: sanitizedProfile.address?.country || 'United States'
                },
                profileComplete: profileData.profileComplete || false,
                updatedAt: serverTimestamp()
            };

            console.log('Updating user document with data:', updateData);

            // Update the user document in Firestore
            const userRef = doc(db, 'users', user.uid);
            await setDoc(userRef, updateData, { merge: true });

            // Update local state
            const updatedUser = {
                ...user,
                ...updateData,
                fullName: `${updateData.firstName} ${updateData.lastName}`.trim(),
                displayName: updateData.firstName ?
                    `${updateData.firstName} ${updateData.lastName}`.trim() :
                    user.email?.split('@')[0] || 'User'
            };

            setUser(updatedUser);

            // Update userProfile state if it exists
            if (userProfile) {
                setUserProfile(prev => ({
                    ...prev,
                    ...updatedUser
                }));
            }

            console.log('User profile updated successfully');
            return updatedUser;

        } catch (error) {
            console.error('Error updating user profile:', error);
            throw new Error(`Failed to update profile: ${error.message}`);
        }
    };

    const createGroup = async (groupName, uid, description = '') => {
        try {
            console.log('Creating new group:', groupName);
            const group = {
                group_name: groupName,
                owner_uid: uid,
                created_at: new Date().toISOString(),
                group_invite: new Date().toISOString(),
                created_accept: new Date().toISOString()
            };
            const newGroup = await supabaseDataService.createGroup(group);
            console.log('Creating new group newGroup:', newGroup);
            await supabaseDataService.addMemberToGroup(newGroup.group_id, uid, 1);
            console.log('added group member');
            return newGroup.group_id;
        } catch (error) {
            console.error('Error creating group:', error);
            throw error;
        }
    };

    const addUserToGroup = async (uid, groupName) => {
        try {
            console.log('Adding user to group:', uid, groupName);

            const group = await supabaseDataService.getGroupByName(groupName);
            console.log('Adding user to group: group:', group);
            if (!_.isEmpty(group)) {
                await supabaseDataService.addMemberToGroup(group[0].group_id, uid, 0);
            }

        } catch (error) {
            console.error('Error adding user to group:', error);
            throw error;
        }
    };

    const removeUserFromGroup = async (userIdToRemove, groupName) => {
        try {
            // Check if current user is admin
            if (!user?.isGroupAdmin) {
                throw new Error('Only group admins can remove users');
            }

            // Prevent admin from removing themselves
            if (userIdToRemove === user.uid) {
                throw new Error('Admins cannot remove themselves from the group');
            }

            console.log('Removing user from group:', userIdToRemove, groupName);

            // Get the user document
            const userRef = doc(db, 'users', userIdToRemove);
            const userDoc = await getDoc(userRef);

            if (!userDoc.exists()) {
                throw new Error('User not found');
            }

            const userData = userDoc.data();
            const currentGroups = userData.groups || [];

            // Check if user is in the group
            if (!currentGroups.includes(groupName)) {
                throw new Error('User is not a member of this group');
            }

            // Remove the group from user's groups array
            const updatedGroups = currentGroups.filter(group => group !== groupName);

            // Update user document
            await updateDoc(userRef, {
                groups: updatedGroups,
                updatedAt: serverTimestamp()
            });

            // Update the group document to remove the user from members array
            const groupQuery = query(
                collection(db, 'groups'),
                where('name', '==', groupName)
            );
            const groupSnapshot = await getDocs(groupQuery);

            if (!groupSnapshot.empty) {
                const groupDoc = groupSnapshot.docs[0];
                const groupRef = doc(db, 'groups', groupDoc.id);

                await updateDoc(groupRef, {
                    members: arrayRemove(userIdToRemove),
                    updatedAt: serverTimestamp()
                });
            }

            console.log('User removed from group successfully');
            return true;

        } catch (error) {
            console.error('Error removing user from group:', error);
            throw error;
        }
    };

    const getAllGroupsCached = async () => {
        const now = Date.now();

        // Return cached data if it's still fresh
        if (groupsCache && groupsCacheTimestamp && (now - groupsCacheTimestamp < CACHE_DURATION)) {
            console.log('Returning cached groups');
            return groupsCache;
        }

        // Fetch fresh data
        const groups = await getAllGroups();
        setGroupsCache(groups);
        setGroupsCacheTimestamp(now);

        return groups;
    };

    const getAllGroups = async () => {
        try {
            console.log('Fetching all groups');
            const querySnapshot = await getDocs(collection(db, 'groups'));
            const groups = [];
            querySnapshot.forEach((doc) => {
                groups.push({ id: doc.id, ...doc.data() });
            });
            console.log('Groups fetched:', groups.length);
            return groups;
        } catch (error) {
            console.error('Error getting groups:', error);
            throw error;
        }
    };

    const belongsToGroup = (groupName) => {
        const belongs = userGroups.includes(groupName);
        console.log('User belongs to group', groupName, ':', belongs);
        return belongs;
    };

    const validateSubdomain = async () => {
        const subdomain = subdomainValidation.subdomain;

        setSubdomainValidation(prev => ({
            ...prev,
            isValidating: true,
            error: null
        }));

        try {
            // No subdomain is always valid
            if (!subdomain) {
                setSubdomainValidation(prev => ({
                    ...prev,
                    subdomain,
                    isValid: true,
                    isValidating: false
                }));
                return;
            }

            const group = await supabaseDataService.getGroupByName(subdomain);
            console.log('----subdomain->',subdomain);
            console.log('----group->',group);

            const isValid = !_.isEmpty(group);
            setSubdomainValidation(prev => ({
                ...prev,
                subdomain,
                isValid,
                isValidating: false
            }));

            console.log('Subdomain validation result:', { subdomain, isValid });
        } catch (error) {
            console.error('Error validating subdomain:', error);

            // Fall back to default validation on error
            const defaultAllowed = ['randy', 'admin', 'www'];
            const isValid = defaultAllowed.includes(subdomain);

            setSubdomainValidation(prev => ({
                ...prev,
                subdomain,
                isValid,
                isValidating: false,
                error: error.message
            }));
        }
    };

    const refreshSubdomainValidation = async () => {
        setGroupsCache(null);
        setGroupsCacheTimestamp(null);
        await validateSubdomain();
    };

    const checkEmailExists = async (email) => {
        try {
            console.log('Checking if email exists in Firestore:', email);
            const normalizedEmail = email.toLowerCase().trim();

            const usersQuery = query(
                collection(db, 'users'),
                where('email', '==', normalizedEmail)
            );

            const querySnapshot = await getDocs(usersQuery);
            const exists = !querySnapshot.empty;

            console.log('Email check result:', { email: normalizedEmail, exists });
            return exists;

        } catch (error) {
            console.error('Error checking email existence:', error);

            // If we can't check due to permissions or other issues,
            // return false to allow signup attempt
            // The signup process will catch duplicate emails
            return false;
        }
    };

    const getCategories = async () => {
        try {
            if (!categories || categories.length === 0) {
                await loadCategories();
            }
            return categories;
        } catch (error) {
            console.error('Error getting categories:', error);
            throw error;
        }
    };

    const value = {
        // User data
        user,                    // Firebase user object
        userProfile,            // Complete user profile
        userGroups,

        // Utility functions
        getUserId,
        getUserEmail,
        getUserDisplayName,
        getUserFullName,
        isEmailVerified,
        isGroupAdmin,

        // Auth functions
        login,
        logout,
        signup,
        updateUserProfile,
        checkEmailExists,
        checkAuthState,

        // Group functions
        createGroup,
        addUserToGroup,
        removeUserFromGroup,
        getAllGroups,
        getAllGroupsCached,
        belongsToGroup,

        // Category functions
        getCategories,

        // State
        loading,
        authError,

        // Subdomain validation
        subdomainValidation,
        validateSubdomain,
        refreshSubdomainValidation
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
}
